// src/components/buyer-leads/NewBuyerLeadModal.jsx
import React, { useState, useRef } from "react";
import { X, Check } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import AddClientPicker from "../new-contact/AddClientPicker";
import ContactEditDrawer from "../new-contact/ContactEditDrawer";
import BuyerNeedsForm from "./BuyerNeedsForm";
import { maybeSendNewLeadAutoReply } from "../../utils/texting";
import {
  saveBuyerLead,
  getContactsByIds,
  setContactReferrer,
} from "../../utils/firebasefunctions";
import ReferredByPicker from "../lead-tracker/ReferredByPicker";
import { addContact } from "../../utils/contactsApi";
import { normalizeContact } from "../../utils/contactNormalize";
import { addCoClient, removeClient, listClients } from "../../utils/leadClients";
import { notifyOnNewLead, emailListingRequestRecipients } from "../../utils/helper";
import { createSubmitGuard } from "../../utils/submitGuard";
import { requestBuyerFile } from "../../utils/buyerFileRequest";
import { sendBuyerPortalForms } from "../../utils/sendBuyerPortalForms";

const emptyLead = (user) => ({
  contactId: "",
  contactName: "",
  coClients: [],
  priceMin: null,
  priceMax: null,
  preApproval: "",
  preApprovalAmount: null,
  areas: [],
  propertyTypes: [],
  bedsMin: null,
  bathsMin: null,
  timeline: "",
  financingType: "",
  sqftMin: null,
  mustHaves: [],
  motivation: "",
  mustSellFirst: "",
  assignedAgent: user?.id || "",
  assignedAgentName: [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.name || "",
  nextFollowUp: "",
  leadSource: "",
  notes: "",
  stage: "New Lead",
  fubSync: false,
});

export default function NewBuyerLeadModal({ onClose }) {
  const [{ user, leadsource, employees, users }] = useStateValue();
  const [lead, setLead] = useState(() => emptyLead(user));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [coPickerOpen, setCoPickerOpen] = useState(false);
  const [creatingContact, setCreatingContact] = useState(false);
  const [creatingFor, setCreatingFor] = useState("primary");
  const [saving, setSaving] = useState(false);
  const [requestFile, setRequestFile] = useState(false);
  const [sendForms, setSendForms] = useState(false);
  const [referrer, setReferrer] = useState(null); // { contactId, name } | null
  const [referrerPickerOpen, setReferrerPickerOpen] = useState(false);

  const onChange = (patch) => setLead((prev) => ({ ...prev, ...patch }));

  const attachContact = async (contactId) => {
    setPickerOpen(false);
    try {
      const [raw] = await getContactsByIds([contactId]);
      const c = normalizeContact(raw);
      onChange({ contactId, contactName: c?.name || "" });
    } catch (e) {
      onChange({ contactId });
    }
  };

  const attachCoClient = async (contactId) => {
    setCoPickerOpen(false);
    try {
      const [raw] = await getContactsByIds([contactId]);
      const c = normalizeContact(raw);
      setLead((prev) => addCoClient(prev, { contactId, contactName: c?.name || "" }));
    } catch (e) {
      setLead((prev) => addCoClient(prev, { contactId, contactName: "" }));
    }
  };

  // ContactEditDrawer.onSubmit hands back the form; persist via addContact
  // (mirrors add-deal's handleCreateNewContact). addContact returns { firebaseId }.
  const handleCreatedContact = async (form) => {
    if (!user?.gmailtoken?.access_token) {
      toast.error("Connect Gmail in Settings before adding a new contact");
      return;
    }
    try {
      const created = await addContact({ user, contact: form });
      setCreatingContact(false);
      if (created?.firebaseId) {
        if (creatingFor === "co") await attachCoClient(created.firebaseId);
        else await attachContact(created.firebaseId);
      }
    } catch (e) {
      toast.error(e.message || "Failed to create contact");
    }
  };

  // Same narrow race as the listing modal: the button disables on re-render, so
  // two clicks in one tick could both get through.
  const saveGuard = useRef(createSubmitGuard());

  const handleSave = () => saveGuard.current(saveLead);

  const saveLead = async () => {
    if (!lead.contactId) {
      toast.error("Please select a client first");
      return;
    }
    setSaving(true);
    // Register the lead under its assigned agent (so it shows under that agent,
    // not whoever entered it). Keep the actual creator as enteredBy for audit.
    const ownerId = lead.assignedAgent || user?.id;
    const res = await saveBuyerLead({
      ...lead,
      createdby: ownerId,
      enteredBy: user?.id,
    });
    if (!res.success) {
      setSaving(false);
      toast.error(res.error || "Failed to save lead");
      return;
    }
    if (referrer) {
      setContactReferrer(lead.contactId, referrer).then((r) => {
        if (!r.success) toast.warn("Lead saved, but the referrer link failed");
      });
    }
    notifyOnNewLead({ ...lead, id: res.id, createdby: ownerId }, users, "buyer").catch(() => {});
    // Instant auto-reply text (only fires when switched on in Settings).
    maybeSendNewLeadAutoReply({
      leadId: res.id,
      contactId: lead.contactId,
      contactName: lead.contactName,
    }).catch(() => {});

    if (requestFile) {
      const savedLead = { ...lead, id: res.id, createdby: ownerId, assignedAgent: ownerId };
      // Email the Settings → Notifications buyer-request recipients (shape the
      // lead like a deal for the helper).
      emailListingRequestRecipients(
        {
          dealtype: "Buyer",
          customers: listClients(lead)
            .map((c) => c.contactId)
            .filter(Boolean),
          clientLastName: lead.contactName || "",
          agentData: ownerId,
          notes: lead.notes || "",
          createdat: res.id,
        },
        user,
      ).catch((err) =>
        console.error("Buyer request email blast failed:", err),
      );
      // Mint the hidden buyer file (which provisions a Drive folder via the
      // deal trigger) and link it to the lead; optionally email the buyer forms.
      try {
        const { deal } = await requestBuyerFile(savedLead, user, { sendForms });
        if (sendForms && deal) {
          const sent = await sendBuyerPortalForms(deal, savedLead, user);
          if (!sent.ok && sent.reason)
            toast.info(`File created. Forms not sent: ${sent.reason}.`);
        }
      } catch (e) {
        console.error("Buyer file creation failed:", e);
        toast.error(
          "Lead saved, but building the file failed — you can Request a File from the lead.",
        );
      }
    }

    setSaving(false);
    toast.success(
      requestFile ? "Buyer Lead created · file requested" : "Buyer Lead created",
    );
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[88vh] overflow-hidden flex flex-col pointer-events-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Add Buyer Lead</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Client</label>
              {lead.contactId ? (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-slate-800">{lead.contactName || "Selected contact"}</span>
                  <button className="text-xs text-linkColor" onClick={() => setPickerOpen(true)}>Change</button>
                </div>
              ) : (
                <button onClick={() => setPickerOpen(true)}
                  className="w-full border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-600 hover:border-slate-400">
                  + Select or Create a Client
                </button>
              )}
            </div>

            {lead.contactId && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Co-Clients</label>
                {listClients(lead)
                  .filter((c) => !c.primary)
                  .map((c) => (
                    <div key={c.contactId} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-1">
                      <span className="text-sm text-slate-700">{c.contactName || "Selected contact"}</span>
                      <button className="text-xs text-slate-400 hover:text-red-500"
                        onClick={() => setLead((prev) => removeClient(prev, c.contactId))}>Remove</button>
                    </div>
                  ))}
                <button onClick={() => setCoPickerOpen(true)}
                  className="w-full border border-dashed border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-600 hover:border-slate-400">
                  + Add Co-Client
                </button>
              </div>
            )}

            <div className="mt-3">
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Referred by (optional)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700">
                  {referrer ? referrer.name || "Selected contact" : "No referrer"}
                </span>
                <button
                  type="button"
                  className="text-xs text-linkColor hover:underline"
                  onClick={() => setReferrerPickerOpen(true)}
                >
                  {referrer ? "Change" : "Add"}
                </button>
                {referrer && (
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:underline"
                    onClick={() => setReferrer(null)}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <BuyerNeedsForm value={lead} onChange={onChange} leadSources={leadsource || []} agents={employees || []} />

            <div className="border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setRequestFile((v) => !v)}
                className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold border transition ${
                  requestFile
                    ? "bg-[#1E293B] border-[#1E293B] text-white"
                    : "bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                }`}
              >
                <Check size={16} className={requestFile ? "" : "opacity-40"} />
                Also Request a File for This Buyer
              </button>
              <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                Creates this buyer's file + Drive folder now (hidden from the board) so you can collect the signed agreement — the buyer still shows in the Buyers Pipeline.
              </p>

              {requestFile && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setSendForms((v) => !v)}
                    className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold border transition ${
                      sendForms
                        ? "bg-[#1E293B] border-[#1E293B] text-white"
                        : "bg-white border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                    }`}
                  >
                    <Check size={16} className={sendForms ? "" : "opacity-40"} />
                    Also Send Buyer Forms
                  </button>
                  <p className="text-[11px] text-slate-400 mt-1.5 text-center">
                    Emails this buyer a link to their online forms — even before they've signed.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-600">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2 text-sm rounded-lg bg-[#1E293B] text-white font-semibold disabled:opacity-60">
              {saving ? "Saving…" : "Create Lead"}
            </button>
          </div>
        </div>
      </div>

      {pickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setPickerOpen(false)}
          onPick={attachContact}
          onCreateNew={() => { setPickerOpen(false); setCreatingFor("primary"); setCreatingContact(true); }}
        />
      )}
      {coPickerOpen && (
        <AddClientPicker
          userId={user.id}
          existingCustomerIds={listClients(lead).map((c) => c.contactId)}
          onClose={() => setCoPickerOpen(false)}
          onPick={attachCoClient}
          onCreateNew={() => { setCoPickerOpen(false); setCreatingFor("co"); setCreatingContact(true); }}
        />
      )}
      {creatingContact && (
        <ContactEditDrawer
          onClose={() => setCreatingContact(false)}
          onSubmit={handleCreatedContact}
        />
      )}
      {referrerPickerOpen && (
        <ReferredByPicker
          excludeContactId={lead.contactId}
          onClose={() => setReferrerPickerOpen(false)}
          onDone={(r) => {
            setReferrer(r);
            setReferrerPickerOpen(false);
          }}
        />
      )}
    </>
  );
}
